import { createFileRoute, Outlet, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { toast } from 'sonner';
import useChart from '../../hooks/useChart';
import usePatientDoc from '../../hooks/usePatientDoc';
import { Chart, PatientDoc } from '@usacsmideo/types';
import { MfaRouteGate } from '../../components/mfa/MfaRouteGate';
import { useMfaRouting } from '../../hooks/useMfaRouting';
import { isChartPermitted, isChartWithheld } from '../../utils/mfa/chartGating';
import { ConsentGateModal } from '../../components/ConsentGateModal';
import { setInitialUnifiedNavObject } from '../../data/navigation/setInitialUnifiedNavObj';
import { useCurrentUserPersona } from '../../hooks/useCurrentUserPersona';

export const Route = createFileRoute('/_authenticated/$patientId')({
  beforeLoad: async ({ context, params }) => {
    const { firebaseGetUser, firebaseSignOut } = context.auth;
    const currentUser = await firebaseGetUser();

    // If not authenticated, the parent _authenticated layout handles the redirect.
    // This guard only handles the UID mismatch case.
    if (currentUser && currentUser.uid !== params.patientId) {
      // Store in sessionStorage (not localStorage) so the URL is available if the
      // correct user logs in on this tab, but auto-expires when the tab closes —
      // no stale redirects in future sessions.
      sessionStorage.setItem('requestedURL', window.location.href);
      sessionStorage.setItem('uidMismatch', 'true');
      await firebaseSignOut();
      throw redirect({ to: '/login' });
    }
  },
  component: PatientIdLayout,
});

// Routes that do NOT require consent to be completed.
// Everything else is gated by default — new routes are automatically gated.
// Prefix patterns: any route starting with this path is exempt.
const CONSENT_EXEMPT_PREFIXES = [
  '/info/consentForms',
  '/onboarding',
];

// Exact suffix patterns: only the exact path (under /{patientId}) is exempt.
const CONSENT_EXEMPT_EXACT_SUFFIXES = [
  '/profile',
  '/info',
];

function isConsentExempt(pathname: string, patientId: string): boolean {
  // Dashboard index is exempt (section screen entry points)
  const patientRoot = `/${patientId}`;
  if (pathname === patientRoot || pathname === `${patientRoot}/`) return true;

  // Check prefix patterns (e.g., /onboarding matches /onboarding/learn)
  if (CONSENT_EXEMPT_PREFIXES.some((pattern) => pathname.includes(pattern))) return true;

  // Check exact suffix patterns (e.g., /info but NOT /info/files)
  const suffix = pathname.replace(patientRoot, '');
  return CONSENT_EXEMPT_EXACT_SUFFIXES.includes(suffix);
}

// NavObj recovery state machine. CU-86ahkkbmb W1: legacy patients (created
// before navObj landed) need their navObj seeded by the setInitialNavObject CF
// before any authenticated route mounts. The previous fire-and-forget recovery
// on home/index was bypassed by deep-link redirects and silently failed.
type NavObjRecoveryStatus = 'idle' | 'recovering' | 'failed';

function PatientIdLayout() {
  const { currentUser, firebaseSignOut } = useAuth();
  const navigate = useNavigate();
  const { patientId } = Route.useParams();
  const isSigningOut = useRef(false);
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  // ⚠️ ORDER MATTERS HERE (CU-86ak77fuv, B1). The patient doc comes FIRST because
  // the MFA decision is derived from it, and the chart listener is then gated on
  // that decision. The patient doc itself is NOT gated by the Firestore rules —
  // only the PHI subcollections (Chart, video, datesOfService, …) are — so this
  // listener resolves normally for a gated patient. That is what makes the
  // decision available before we ever ask for the chart.
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc | undefined;
  const patientDocResolved = patientDocQuery.hasResolved;

  // The PHI gate decision, computed HERE rather than only inside MfaRouteGate.
  //
  // ⚠️ `MfaRouteGate` sits BELOW the navObj-recovery and consent gates, both of
  // which read chart data. A brand-new enforced patient whose chart is denied
  // would reach those gates with `chart === undefined` and never get to the
  // enrol screen. Lifting the decision up lets us (a) not subscribe at all when
  // gated, and (b) let those gates fall through instead of blocking.
  const { status: mfaStatus } = useMfaRouting(patientInfo);

  // ⚠️ SUBSCRIBE ONLY ON A DECIDED PASS. `resolving` must NOT subscribe and must
  // NOT be treated as denied — the gate is fail-closed by design, and deciding
  // on an unresolved doc reads `enforced: false`, waving a gated patient
  // through. `exception-bypass` is a real pass: those patients may see PHI.
  const chartPermitted = isChartPermitted(mfaStatus);

  const chartQuery = useChart(patientId, {
    enabled: chartPermitted,
    // Distinguishes "withheld" from "still loading" for the gates below.
    withheldByPolicy: isChartWithheld(mfaStatus),
  });
  const chart = chartQuery.data as Chart;

  // True when the chart is not merely absent but NOT PERMITTED. The gates below
  // must fall through on this rather than block — a spinner or a consent modal
  // in front of a patient who needs the enrol screen is a dead end.
  const chartWithheld = chartQuery.isWithheld;

  // ECN Subtask 8 (CU-86ahjxqvp) — persona route guard (defense-in-depth).
  // An agent-only user (isMIDEOFamilyMember && !isPatient) has no chart; the
  // navObj-recovery and consent gates below both read chart data and would
  // crash/misbehave for them. Chrome gating already hides every affordance into
  // these screens, but if an agent reaches a $patientId URL anyway (stale
  // bookmark, deep link), redirect them to /sessions BEFORE any chart-reading
  // gate fires. We wait for personaResolved so we never flash on the first frame.
  const { isPatient, isMIDEOFamilyMember, personaResolved } = useCurrentUserPersona();
  const isAgentOnly = personaResolved && isMIDEOFamilyMember && !isPatient;

  const [recoveryStatus, setRecoveryStatus] = useState<NavObjRecoveryStatus>('idle');
  // Ref guards against double-firing the CF when the listener re-emits while
  // the call is still in flight — without it, every onSnapshot tick during
  // recovery would queue another CF invocation.
  const recoveryInFlight = useRef(false);

  // Derive consent state from hipaaConsentState field (CU-86agmta3g).
  // Fallback to signature1DataURI for pre-migration patients (removed in CU-86agtbw7m).
  const consentStatus = chart?.HIPAA_AUTH?.hipaaConsentState?.status;
  const consentComplete = consentStatus
    ? consentStatus === 'granted'
    : Boolean(chart?.HIPAA_AUTH?.signature1DataURI);

  // Component-level fallback: beforeLoad doesn't reliably fire for child routes
  // with dynamic segments ($patientId) on all browsers (see _authenticated.tsx).
  useEffect(() => {
    if (!currentUser || currentUser.uid === patientId || isSigningOut.current) return;

    isSigningOut.current = true;
    sessionStorage.setItem('requestedURL', window.location.href);
    sessionStorage.setItem('uidMismatch', 'true');

    firebaseSignOut().then(() => {
      navigate({ to: '/login' });
    });
  }, [currentUser, patientId, firebaseSignOut, navigate]);

  // ECN Subtask 8 — redirect agent-only personas to /sessions (their whole shell).
  useEffect(() => {
    if (isAgentOnly) {
      navigate({ to: '/sessions' });
    }
  }, [isAgentOnly, navigate]);

  // Detect legacy patients (missing navObj) and recover by calling
  // setInitialNavObject. The Firestore listener in usePatientDoc will pick up
  // the new navObj automatically — no manual refetch needed.
  useEffect(() => {
    if (!patientDocResolved || !patientInfo) return;
    if (patientInfo.navObj !== undefined) return;
    if (recoveryInFlight.current) return;

    recoveryInFlight.current = true;
    setRecoveryStatus('recovering');

    Sentry.captureMessage('legacy-patient-detected: missing navObj', {
      level: 'warning',
      extra: { patientId },
    });

    setInitialUnifiedNavObject(patientId)
      .then(() => {
        // Listener will re-emit with the new navObj; status flips back to idle
        // so the gate re-evaluates and renders <Outlet />.
        recoveryInFlight.current = false;
        setRecoveryStatus('idle');
      })
      .catch((error) => {
        Sentry.captureException(error, {
          extra: { context: 'navObj-recovery-failed', patientId },
        });
        toast.error(
          'We could not load your navigation. Please refresh the page or contact support if this issue persists.',
          { duration: 10000 },
        );
        recoveryInFlight.current = false;
        setRecoveryStatus('failed');
      });
  }, [patientDocResolved, patientInfo, patientId]);

  // While signing out, render nothing to prevent child components from mounting.
  if (!currentUser || currentUser.uid !== patientId) {
    return null;
  }

  // ECN Subtask 8 — render nothing for an agent-only user while the redirect
  // effect above sends them to /sessions. This MUST precede the navObj-recovery
  // and consent gates below (both read chart data a chart-less agent lacks).
  if (isAgentOnly) {
    return null;
  }

  // NavObj recovery gate — runs BEFORE the consent gate. Downstream nav
  // utilities (getNextPage, setDisabledPages, getValidScreenArray) assume
  // navObj is present, so we must not render <Outlet /> until it is.
  // ⚠️ CU-86ak77fuv — `!chartWithheld` guard. A gated patient must reach the
  // enrol screen, not a "Preparing your navigation…" spinner they can never get
  // past. This matters most for BULK-enrolled patients: `createPatientDocs`
  // writes no `navObj`, so they hit this gate on their very first login, and
  // with the chart withheld the recovery CF's result cannot be observed here
  // anyway. The recovery still runs (the effect above is unchanged) — we simply
  // do not BLOCK on it while PHI is withheld.
  if (!chartWithheld && patientDocResolved && patientInfo && patientInfo.navObj === undefined) {
    if (recoveryStatus === 'failed') {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
          <p className="mb-4 text-lg font-semibold">We could not load your navigation.</p>
          <p className="text-gray-600">
            Please refresh the page. If the problem persists, contact support.
          </p>
        </div>
      );
    }
    // Spinner matches the pattern used in DocuSignSigBox, WebCamVideo, and selfPay.
    // No shared Loader component exists in this repo — each consumer inlines it.
    return (
      <div
        role="status"
        className="flex min-h-screen flex-col items-center justify-center px-4 text-center"
      >
        <svg
          aria-hidden="true"
          className="mb-4 inline h-10 w-10 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span>Preparing your navigation…</span>
      </div>
    );
  }

  // Consent gate: block non-exempt routes if consent is incomplete.
  // Wait for chart data to load before gating (don't flash the modal).
  // ⚠️ CU-86ak77fuv — `!chartWithheld` is load-bearing, not defensive.
  //
  // `consentStatus` is derived from `chart?.HIPAA_AUTH?...`. When the chart is
  // withheld by the MFA gate the chart is undefined, so `consentComplete` reads
  // FALSE — "no consent on record" — for a patient who may well have consented.
  // Gating on that would show a consent modal to a patient who needs the enrol
  // screen, and they could never satisfy it: reaching the consent form requires
  // reading the chart, which is exactly what is being withheld.
  //
  // ⚠️ THIS IS NOT A CONSENT BYPASS. `MfaRouteGate` below renders the enrol
  // screen INSTEAD OF `<Outlet />`, so no PHI route mounts. Once the patient
  // enrols, the chart listener re-subscribes, `chartWithheld` goes false, and
  // this gate evaluates normally against real chart data — before any content
  // is reachable. Consent is deferred by one step, never skipped.
  const shouldGate =
    !chartWithheld &&
    chartQuery.isFetched &&
    !consentComplete &&
    !isConsentExempt(pathname, patientId);

  if (shouldGate) {
    return (
      <ConsentGateModal
        onNavigateToConsent={() => navigate({ to: `/${patientId}/info/consentForms` })}
        onNavigateToProfile={() => navigate({ to: `/${patientId}/profile` })}
        consentStatus={consentStatus}
      />
    );
  }

  // MFA gate (CU-86ajwpnek §9). ⚠️ SHIPS INERT: pre-cutover `mfa.enforced` is
  // unset, so `decideMfaGate` branch 2 returns "pass" for every patient and
  // this renders <Outlet /> untouched. It is in place and dormant, ready for
  // the cutover sweep.
  //
  // ⚠️ Placed LAST, after the consent gate, deliberately. Consent is a legal
  // precondition for holding the data at all, so it must not be reachable only
  // by first satisfying MFA. Placed here, every authenticated patient render —
  // interactive login, reload, deep link — passes through this one point.
  return (
    <MfaRouteGate patientDoc={patientInfo}>
      <Outlet />
    </MfaRouteGate>
  );
}
